// ===========================================
// Newsletter Preview Page
// ===========================================
// Preview newsletter content before publishing

import { useNavigate, useParams, Link } from 'react-router-dom';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Typography,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useNewsletter, usePublishNewsletter } from '../../hooks/useNewsletter';

// ===========================================
// COMPONENT
// ===========================================

export default function NewsletterPreviewPage() {
  const navigate = useNavigate();
  const { id, schoolSlug } = useParams<{ id: string; schoolSlug: string }>();

  const { data: newsletter, isLoading, error } = useNewsletter(id || '');
  const publishMutation = usePublishNewsletter();

  const handlePublish = async () => {
    if (!id) return;
    try {
      await publishMutation.mutateAsync(id);
      navigate(`/${schoolSlug}/admin/newsletters/${id}`);
    } catch (err) {
      console.error('Failed to publish newsletter:', err);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !newsletter) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load newsletter preview
        </Alert>
        <Button
          startIcon={<BackIcon />}
          component={Link}
          to={`/${schoolSlug}/admin/newsletters`}
          sx={{ mt: 2 }}
        >
          Back to Newsletters
        </Button>
      </Box>
    );
  }

  const canPublish = newsletter.status === 'DRAFT' && newsletter.sections.length > 0;

  return (
    <Box>
      <PageHeader
        title="Newsletter Preview"
        subtitle={newsletter.title}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<BackIcon />}
              component={Link}
              to={`/${schoolSlug}/admin/newsletters/${id}`}
            >
              Back to Editor
            </Button>
            <Button
              startIcon={<EditIcon />}
              component={Link}
              to={`/${schoolSlug}/admin/newsletters/${id}`}
            >
              Edit
            </Button>
            {canPublish && (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handlePublish}
                disabled={publishMutation.isPending}
              >
                {publishMutation.isPending ? 'Publishing...' : 'Publish'}
              </Button>
            )}
          </Stack>
        }
      />

      {/* Newsletter metadata */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Chip label={newsletter.status} size="small" />
          <Chip label={newsletter.visibility} size="small" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            By {newsletter.author.firstName} {newsletter.author.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(newsletter.createdAt).toLocaleDateString()}
          </Typography>
        </Stack>
      </Paper>

      {/* Email preview frame */}
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'grey.100', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Email Preview
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 600, mx: 'auto', p: 4 }}>
          {/* Email header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {newsletter.title}
            </Typography>
            {newsletter.excerpt && (
              <Typography variant="body1" color="text.secondary">
                {newsletter.excerpt}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Sections */}
          {newsletter.sections.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No content sections. Add sections in the editor.
            </Typography>
          ) : (
            newsletter.sections
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((section, index) => (
                <Box key={section.id} sx={{ mb: 4 }}>
                  {section.title && (
                    <Typography variant="h6" gutterBottom>
                      {section.title}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      '& img': { maxWidth: '100%', height: 'auto' },
                      '& a': { color: 'primary.main' },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                  />
                  {index < newsletter.sections.length - 1 && (
                    <Divider sx={{ mt: 3 }} />
                  )}
                </Box>
              ))
          )}

          <Divider sx={{ my: 3 }} />

          {/* Email footer */}
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              Music 'n Me
            </Typography>
            <Typography variant="caption">
              You received this email because you are subscribed to our newsletter.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Distribution info */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Distribution Settings
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
              Send to:
            </Typography>
            <Typography variant="body2">
              {newsletter.sendToAll ? 'All parents' : 'Selected groups'}
            </Typography>
          </Stack>
          {!newsletter.sendToAll && (
            <>
              {newsletter.targetAgeGroups.length > 0 && (
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                    Age Groups:
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {newsletter.targetAgeGroups.map((group) => (
                      <Chip key={group} label={group} size="small" />
                    ))}
                  </Stack>
                </Stack>
              )}
              {newsletter.targetInstruments.length > 0 && (
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                    Instruments:
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {newsletter.targetInstruments.map((instrument) => (
                      <Chip key={instrument} label={instrument} size="small" />
                    ))}
                  </Stack>
                </Stack>
              )}
            </>
          )}
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
              Total Recipients:
            </Typography>
            <Typography variant="body2">{newsletter.totalRecipients}</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
